'use client';

import { useQuery } from '@tanstack/react-query';
import Decimal from 'decimal.js';

import { useAuthStore } from 'lib/stores/authStore';
import type { WithdralLimitsResponse, WithdrawalSource } from 'models/reqres';
import { getWithdrawalLimits } from 'transport/domains/accountLimits';
import type { WithdrawalLimitItem, WithdrawalLimits } from 'utils/payments';

const CENTICENTS_PER_DOLLAR = 10000;

function toWithdrawalLimitItems(items: WithdralLimitsResponse['card'] | undefined): WithdrawalLimitItem[] {
    return (items ?? []).map((item) => ({
        amountInDollars: item.amount / CENTICENTS_PER_DOLLAR,
        daysLeft: item.days_left,
    }));
}

function toWithdrawalLimitItemsById(
    record: WithdralLimitsResponse['bank_accounts'] | undefined,
): Record<string, WithdrawalLimitItem[]> {
    const limitsById: Record<string, WithdrawalLimitItem[]> = {};
    Object.entries(record ?? {}).forEach(([key, value]) => {
        limitsById[key] = toWithdrawalLimitItems(value);
    });
    return limitsById;
}

export function mapWithdrawalLimits(response: WithdralLimitsResponse): WithdrawalLimits {
    return {
        card: toWithdrawalLimitItems(response.card),
        crypto: toWithdrawalLimitItems(response.crypto),
        stripe: toWithdrawalLimitItems(response.stripe),
        paypal: toWithdrawalLimitItems(response.paypal),
        venmo: toWithdrawalLimitItems(response.venmo),
        savedCards: toWithdrawalLimitItemsById(response.saved_cards),
        bankAccounts: toWithdrawalLimitItemsById(response.bank_accounts),
        plaidBankAccounts: toWithdrawalLimitItemsById(response.plaid_bank_accounts),
    };
}

export const withdrawalLimitsKeys = {
    all: ['withdrawalLimits'] as const,
    user: (userId: string | null) => ['withdrawalLimits', userId] as const,
    bySource: (userId: string | null, source: WithdrawalSource) => ['withdrawalLimits', userId, source] as const,
};

export function fetchWithdrawalLimits(userId: string, source: WithdrawalSource): Promise<WithdrawalLimits> {
    return new Promise((resolve, reject) => {
        let settled = false;
        getWithdrawalLimits(
            userId,
            // Omit the default source so the event-contract request shape stays
            // identical to what the backend has always received.
            source === 'event_contract' ? undefined : source,
            (limits) => {
                settled = true;
                resolve(mapWithdrawalLimits(limits));
            },
            (err) => {
                settled = true;
                reject(err);
            },
            () => {
                // A forced disconnect makes handleErrorCallback swallow the error
                // (no onErr); reject from onFinally so the RQ promise never hangs.
                if (!settled) {
                    settled = true;
                    reject(new Error('withdrawal limits request did not settle'));
                }
            },
        );
    });
}

export function withdrawalLimitsQueryOptions(userId: string, source: WithdrawalSource) {
    return {
        queryKey: withdrawalLimitsKeys.bySource(userId, source),
        queryFn: () => fetchWithdrawalLimits(userId, source),
        // Withdrawal availability changes with deposits, transfers, settlements,
        // and holds. Never let the app-wide 120s default suppress an entry refetch.
        staleTime: 0,
        refetchOnMount: 'always' as const,
    };
}

export function useWithdrawalLimitsQuery(source: WithdrawalSource = 'event_contract') {
    const userId = useAuthStore((s) => s.userId);
    const hasTransfersAccess = useAuthStore((state) => state.derived).hasTransfersAccess();

    return useQuery({
        ...withdrawalLimitsQueryOptions(userId ?? '', source),
        enabled: !!userId && hasTransfersAccess,
    });
}

// Smallest daysLeft across the card + bank-account withdrawal limits, or
// Infinity when limits are not yet loaded. Ported verbatim from the old
// PaymentsStore.minWithdrawalDaysLeft getter (event_contract source).
export function getMinWithdrawalDaysLeft(withdrawalLimits: WithdrawalLimits | undefined): number {
    if (!withdrawalLimits) {
        return Infinity;
    }
    const cardMin = withdrawalLimits.card.reduce((acc, item) => (item.daysLeft < acc ? item.daysLeft : acc), Infinity);
    const bankMin = Object.values(withdrawalLimits.bankAccounts).reduce(
        (acc, items) => items.reduce((acc2, item) => (item.daysLeft < acc2 ? item.daysLeft : acc2), acc),
        Infinity,
    );
    return Math.min(cardMin, bankMin);
}

export function useMinWithdrawalDaysLeft(): number {
    const { data } = useWithdrawalLimitsQuery('event_contract');
    return getMinWithdrawalDaysLeft(data);
}

// Sum of the limits withdrawable today. Centicent balances carry sub-cent
// precision but the backend floors the withdrawable max to whole cents, so
// snap the float sum to the centicent grid (4dp) before flooring to 2dp —
// otherwise binary error in the sum (e.g. 0.29 + 0.58 = 0.8699999999999999)
// drops a real cent, and an unfloored sum overstates the max the backend
// will accept.
export function sumAvailableNowInDollars(items: WithdrawalLimitItem[]): number {
    const total = items
        .filter((item) => item.daysLeft === 0 && item.amountInDollars > 0)
        .reduce((acc, item) => acc + item.amountInDollars, 0);
    return new Decimal(total)
        .toDecimalPlaces(4, Decimal.ROUND_HALF_UP)
        .toDecimalPlaces(2, Decimal.ROUND_DOWN)
        .toNumber();
}
